import axios from 'axios';
import _ from 'lodash';

axios.defaults.baseURL = process.env.VUE_APP_API_URL;

export default class DataSvc {
  /* Authorization */
  static signIn(credentials) {
    return axios.post('users/auth_token', credentials)
      .then((authResponse) => {
        const authToken = authResponse.data.auth_token;

        // set bearer auth token
        axios.defaults.headers.common.Authorization = `Bearer ${authToken}`;

        // grab user corresponding to auth token
        return axios.get('users/me')
          .then((userResponse) => {
            const user = userResponse.data;
            user.auth_token = authToken;
            return user;
          });
      });
  }

  static signUp(user) {
    const data = _.cloneDeep(user);

    data.username = data.email;

    return axios.post(
      'public/users',
      data,
    ).then(() => {
      const credentials = {
        username: user.email,
        password: user.password,
      };

      return this.signIn(credentials);
    });
  }

  static tokenAuth() {
    return axios.get('tests/auth').then(response => response.data);
  }

  static signOut() {
    return Promise.resolve();
  }

  static updateSelf(user) {
    const data = _.cloneDeep(user);
    data.username = data.email;
    return axios.put('users/me', data).then(response => response.data);
  }

  static forgotPassword(data) {
    return axios.post(
      'public/users/password-reset',
      data,
    ).then(response => response.data);
  }
  /* / Authorization */

  /* Events */
  static getEvents(organizationID) {
    const params = {
      organization_id: organizationID,
    };

    return axios.get(
      'public/events',
      { params },
    ).then(response => response.data);
  }

  static getEvent(eventID, hiddenCode) {
    const e = hiddenCode || eventID;
    return axios.get(`public/events/${e}`)
      .then(response => response.data);
  }
  /* / Events */

  /* Registration Session */
  static startRegistrationSession() {
    return axios.post(
      'registration-sessions',
    ).then(response => response.data);
  }

  static getActiveRegistrationSession() {
    return axios.get(
      'registration-sessions/me',
    ).then((response) => {
      const [registrationSession] = response.data;

      if (!registrationSession) {
        return { registrations: [] };
      }

      const statesPromise = axios.get(
        `registration-sessions/${registrationSession.uuid}/registration-states`,
      ).then(({ data }) => data);

      const costsPromise = axios.get(
        `registration-sessions/${registrationSession.uuid}/costs`,
      ).then(({ data }) => data);

      return Promise.all([statesPromise, costsPromise])
        .then((data) => {
          const [states, costs] = data;
          const rs = {};

          rs.uuid = registrationSession.uuid;
          rs.amount_to_charge = costs.amount_to_charge;
          rs.registrations = [];

          registrationSession.registrations.forEach((r) => {
            // base
            const registration = _.pick(r, [
              // base
              'id',
              'waitlist',
              'total_collected',
              'balance_due',
              'form_data',
              'registration_session_uuid',
              'promo_code.code',
              'registered',

              // event
              'event.id',
              'event.name',
              'event.display_start_date_time',
              'event.display_end_date_time',
              'event.partial_payments_open',
              'event.registration_open',
              'event.partial_payments_minimum',

              // pricing package
              'pricing_package.id',
              'pricing_package.label',
              'pricing_package.price_fees_included',
              'pricing_package.is_open',

              // registrant
              'registrant.id',
              'registrant.full_name',
            ]);

            // costs
            registration.cost = _.pick(r, [
              'calc_amount',
              'calc_amount_fees',
              'calc_minimum_payment',
              'calc_amount_total',
            ]);

            registration.cost.amount_to_charge = costs.registrations[r.id];

            // state
            registration.state = states[r.id];

            rs.registrations.push(registration);
          });

          return rs;
        });
    });
  }

  static getClosedRegistrationSessions() {
    const params = {
      closed: true,
    };

    return axios.get(
      'registration-sessions/me',
      { params },
    ).then(({ data }) => {
      let confirmationCode;

      if (data.length > 0) {
        confirmationCode = data[0].short_id;
      }

      const registrations = _(data)
        .map('registrations')
        .flatten()
        .map((r) => {
          const reg = _.pick(r, [
            'id',
            'event.id',
            'event.name',
            'event.display_start_date_time',
            'event.display_end_date_time',
            'event.start_date_time',
            'event.end_date_time',
            'pricing_package.id',
            'pricing_package.label',
            'registrant.id',
            'registrant.full_name',
            'calc_amount_total',
            'calc_minimum_payment',
            'total_collected',
            'balance_due',
            'registration_session_uuid',
            'waitlist',
            'short_id',
            'count_files',
            'registered',
          ]);

          reg.short_id = confirmationCode;

          return reg;
        })
        .value();

      return registrations;
    });
  }

  static deleteRegistrationSession(registrationSessionID) {
    return axios.delete(
      `registration-sessions/${registrationSessionID}`,
    ).then((response) => {
      delete axios.defaults.headers.common.Authorization;
      return response.data;
    });
  }

  static addRegistration(registrationSessionID, registration) {
    const data = _.cloneDeep(registration);

    data.registration_session_uuid = registrationSessionID;

    return axios.post(
      `registration-sessions/${registrationSessionID}/registrations`,
      data,
    ).then(response => response.data);
  }

  static updateRegistration(registrationSessionID, registration) {
    const data = {
      form_data: registration.form_data || {},
    };

    return axios.put(
      `registration-sessions/${registrationSessionID}/registrations/${registration.id}`,
      data,
    ).then(response => response.data);
  }

  static deleteRegistration(registrationSessionID, registrationID) {
    return axios.delete(
      `registration-sessions/${registrationSessionID}/registrations/${registrationID}`,
    ).then(response => response.data);
  }

  static getEventForms(registrationSessionID, eventID) {
    return axios.get(
      `registration-sessions/${registrationSessionID}/events/${eventID}/forms`,
    ).then(response => response.data);
  }

  static createCharge(registrationSessionID, token, partialPayments) {
    const data = { stripe_source_token: null };

    if (token) {
      data.stripe_source_token = token;
    }

    if (partialPayments) {
      data.partial_payments = _.clone(partialPayments);
    }

    return axios.post(
      `registration-sessions/${registrationSessionID}/charges`,
      data,
    ).then(response => response.data);
  }

  static createPaymentCharge(registrationSessionID, payment) {
    return axios.post(
      `registration-sessions/${registrationSessionID}/charges`,
      payment,
    ).then(response => response.data);
  }
  /* / Registration Session */

  /* Promo Codes */
  static applyPromoCode(registrationSessionID, promoCode) {
    const data = {
      promo_codes: promoCode,
    };

    return axios.put(
      `registration-sessions/${registrationSessionID}/promo-codes`,
      data,
    ).then(response => response.data);
  }
  /* / Promo Codes */

  /* Files */
  static uploadRegistrationFile(data) {
    return axios.post(
      'registration-files',
      data,
    ).then(response => response.data);
  }
  /* / Files */

  /* Helpers / Utility */
  static configureErrorHandling(errorHandler) {
    // handle API errors
    axios.interceptors.response.use(
      response => response,
      (error) => {
        const { response } = error;
        errorHandler(response);
        return Promise.reject(response);
      },
    );
  }
  /* / Helpers / Utility */
}
